import { Viewer } from "../main";
import { RayCaster } from "../ray-caster";
import { getPointScale } from "../utilities";
import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  Color,
  DoubleSide,
  EdgesGeometry,
  Euler,
  Group,
  Line,
  Line3,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Plane,
  PlaneGeometry,
  SphereGeometry,
  Vector3
} from "three";
import LocalScene, { SceneCoordinate } from "../projections";

export class ScaleHandle extends Mesh {
  private clippingBox: ClippingBox;

  constructor(clippingBox: ClippingBox, opts) {
    const geometry = new PlaneGeometry(1, 1);
    const material = new MeshBasicMaterial({
      color: opts.color,
      side: DoubleSide,
      opacity: 0.6,
      transparent: true
    });

    super(geometry, material);

    this.clippingBox = clippingBox;
    this.position.copy(opts.position);
    this.lookAt(new Vector3(0,0,0));
    this.scale.set(2.0, 2.0, 2.0);
    this.visible = false;
  }

  get viewer() {
    return this.clippingBox.viewer;
  }

  set highlighted(state) {
    this.visible = state;
  }

  drag(event, point) {
    const raycaster = new RayCaster(this.viewer, event);
    const camera = raycaster.camera;

    // Opposite cube face
    const minusPoint = new Vector3()
      .add(this.position)
      .multiplyScalar(-1.0)
      .applyMatrix4(this.parent.matrix);

    const lineStart = new Vector3()
      .copy(this.parent.position);

    const lineEnd = new Vector3()
      .add(this.position)
      .applyMatrix4(this.parent.matrix);

    const line = new Line3(lineStart, lineEnd);

    const camOnLine = new Vector3();
    line.closestPointToPoint(camera.position, false, camOnLine);

    // Normal from camera to line
    const normal = new Vector3()
      .add(camera.position)
      .sub(camOnLine);

    // Position in direction of plane
    const plane = new Plane();
    plane.setFromNormalAndCoplanarPoint(normal, point);
    const intersect = raycaster.intersectPlane(plane);

    const intersectOnLine = new Vector3();
    line.closestPointToPoint(intersect, false, intersectOnLine);

    // New midpoint
    const midpoint = new Vector3()
      .add(intersectOnLine)
      .add(minusPoint)
      .divideScalar(2.0);

    // Stop movement when the cube would become negative width
    const axis = new Line3(minusPoint, lineEnd);
    const valid = axis.closestPointToPointParameter(midpoint, false) > 0;
    if (!valid) return;

    // Update scale
    const distance = intersectOnLine.distanceTo(minusPoint);
    this.setScale(distance/2.0);

    // Set new position
    this.parent.position.copy(midpoint);
  }

  setScale(newScale) {
    if (this.position.x !== 0) {
      this.parent.scale.x = newScale;
    }

    if (this.position.y !== 0) {
      this.parent.scale.y = newScale;
    }

    if (this.position.z !== 0) {
      this.parent.scale.z = newScale;
    }
  }
}

export class RotateHandle extends Mesh {
  public highlighted
  private clippingBox: ClippingBox;
  private normal: Vector3;
  private ordering;

  constructor(clippingBox: ClippingBox,opts) {
    const geometry = new SphereGeometry(0.1, 32, 32);
    const material = new MeshBasicMaterial({
      color: opts.color
    });

    super(geometry, material);

    this.clippingBox = clippingBox;
    this.normal = opts.normal;
    this.ordering = opts.ordering;
    this.position.copy(opts.position);
  }

  get viewer() {
    return this.clippingBox.viewer;
  }

  drag(event, point) {
    const raycaster = new RayCaster(this.viewer, event);

    const zAxis = new Vector3()
      .copy(this.normal)
      .applyQuaternion(this.parent.quaternion);

    const plane = new Plane();
    plane.setFromNormalAndCoplanarPoint(zAxis, this.parent.position);

    const intersect = raycaster.intersectPlane(plane);
    const projected = new Vector3();
    plane.projectPoint(intersect, projected);

    const xAxis = new Vector3()
      .copy(projected)
      .sub(this.parent.position)
      .setLength(1.0);

    const yAxis = new Vector3()
      .copy(zAxis)
      .cross(xAxis);

    const newAxis = [xAxis, yAxis, zAxis];
    const matrix = new Matrix4().makeBasis(
      newAxis[this.ordering[0]],
      newAxis[this.ordering[1]],
      newAxis[this.ordering[2]]
    );

    const rotation = new Euler().setFromRotationMatrix(matrix);
    this.parent.rotation.copy(rotation);
  }
}

export class PositionHandle extends Mesh {
  public highlighted;
  private clippingBox: ClippingBox;

  constructor(clippingBox: ClippingBox) {
    const geometry = new SphereGeometry(0.1, 32, 32);
    const material = new MeshBasicMaterial({
      color: 0xFFFF00
    });

    super(geometry, material);

    this.clippingBox = clippingBox;
    this.highlighted = false;
  }

  get viewer() {
    return this.clippingBox.viewer;
  }

  get controls() {
    return this.viewer.controls;
  }

  drag(event, point) {
    let {coordinate} = this.controls.getPickerResults(event);
    if (!coordinate) return;

    this.clippingBox.position.copy(coordinate);
  }
}

export class ClippingBox extends Group {
  public viewer: Viewer;
  private wireframe: LineSegments
  private positionHandle: PositionHandle;
  private scaleHandles: ScaleHandle[] = [];
  private rotateHandles: RotateHandle[] = [];
  private wireframeMaterialStandard = new MeshBasicMaterial({color: 0xFFFF00});
  private wireframeMaterialHover = new MeshBasicMaterial({color: 0xFFFFFF});

  public operationID = null;
  public hidePoints = false;
  public updateClassification = false;
  public newClassification = 0;
  public ignoredClassification = 2;

  constructor(viewer: Viewer, operationID=null) {
    super();

    this.viewer = viewer;
    this.operationID = operationID;

    this.addWireframe();
    this.addScaleHandles();
    this.addRotateHandles();
    this.addPositionHandle();
  }

  get boundingBox() {
    const bbox = new Box3()
      .setFromObject(this.wireframe);

    return bbox;
  }

  get attributes() {
    const position = new SceneCoordinate(this.position)
      .toDataProjection()
      .toArray();

    const rotation = this.rotation
      .toArray()
      .slice(0,3);

    // Convert scalar to projection units
    const dataScale = new Vector3()
      .copy(this.scale)
      .divideScalar(LocalScene.dataToMeters)
      .toArray();

    return {
      position,
      rotation,
      scale: dataScale,
      hidePoints: this.hidePoints,
      updateClassification: this.updateClassification,
      newClassification: this.newClassification,
      ignoredClassification: this.ignoredClassification
    };
  }

  setHover(hover) {
    const wireframeMaterial = hover
      ? this.wireframeMaterialHover
      : this.wireframeMaterialStandard;

    this.wireframe.material = wireframeMaterial;
    this.wireframe.material.needsUpdate = true;
  }

  setColor(color) {
    this.wireframeMaterialStandard.color = new Color(color);
  }

  addWireframe() {
    // Wireframe
    const geometry = new EdgesGeometry(new BoxGeometry(2, 2, 2));
    const edgeMesh = new LineSegments(geometry, this.wireframeMaterialStandard);
    this.wireframe = edgeMesh;
    this.add(edgeMesh);

    // X-Axis
    const xAxisMaterial = new LineBasicMaterial({color: 0xFF0000});
    const xAxisGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0,0,0), new Vector3(1,0,0)]);
    const xAxis = new Line(xAxisGeometry, xAxisMaterial);
    this.add(xAxis);

    // Y-Axis
    const yAxisMaterial = new LineBasicMaterial({color: 0x008000});
    const yAxisGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0,0,0), new Vector3(0,1,0)]);
    const yAxis = new Line(yAxisGeometry, yAxisMaterial);
    this.add(yAxis);

    // Z-Axis
    const zAxisMaterial = new LineBasicMaterial({color: 0x0000FF});
    const zAxisGeometry = new BufferGeometry().setFromPoints([
      new Vector3(0,0,0), new Vector3(0,0,1)]);
    const zAxis = new Line(zAxisGeometry, zAxisMaterial);
    this.add(zAxis);
  }

  addScaleHandles() {
    const px = new ScaleHandle(this, {
      color: 0xFF0000,
      position: new Vector3(1,0,0),
    });

    const nx = new ScaleHandle(this, {
      color: 0xFF0000,
      position: new Vector3(-1,0,0),
    });

    const py = new ScaleHandle(this, {
      color: 0x008000,
      position: new Vector3(0,1,0),
    });

    const ny = new ScaleHandle(this, {
      color: 0x008000,
      position: new Vector3(0,-1,0),
    });

    const pz = new ScaleHandle(this, {
      color: 0x0000FF,
      position: new Vector3(0,0,1)
    });

    const nz = new ScaleHandle(this, {
      color: 0x0000FF,
      position: new Vector3(0,0,-1)
    });

    const newObjects = [px, nx, py, ny, pz, nz];
    newObjects.forEach(object => {
      this.scaleHandles.push(object);
      this.add(object);
    });
  }

  addRotateHandles() {
    const xRotate = new RotateHandle(this, {
      color: 0xFF0000,
      rotation: Math.PI/2,
      position: new Vector3(0,1,0),
      normal: new Vector3(1,0,0),
      ordering: [2,0,1]
    });

    const yRotate = new RotateHandle(this, {
      color: 0x008000,
      rotation: 0,
      position: new Vector3(0,0,1),
      normal: new Vector3(0,1,0),
      ordering: [1,2,0]
    });

    const zRotate = new RotateHandle(this, {
      color: 0x0000FF,
      rotation: Math.PI/2,
      position: new Vector3(1,0,0),
      normal: new Vector3(0,0,1),
      ordering: [0,1,2]
    });

    const newObjects = [xRotate, yRotate, zRotate];
    newObjects.forEach(object => {
      this.rotateHandles.push(object);
      this.add(object);
    });
  }

  addPositionHandle() {
    const handle = new PositionHandle(this);
    this.positionHandle = handle;
    this.add(handle);
  }

  setVisible(state) {
    this.positionHandle.highlighted = false;

    this.scaleHandles.forEach(handle => {
      handle.highlighted = state;
    });

    this.rotateHandles.forEach(handle => {
      handle.highlighted = state;
    });
  }

  raycast(raycaster, intersects) {
    this.positionHandle.raycast(raycaster, intersects);

    //return
    this.scaleHandles.forEach(handle => {
      handle.raycast(raycaster, intersects);
    });

    this.rotateHandles.forEach(handle => {
      handle.raycast(raycaster, intersects);
    });
  }

  update() {
    const camera = this.viewer.camera;
    const parentScale = this.scale;

    const objects = [this.positionHandle, ...this.rotateHandles];

    objects.forEach(object => {
      const objectScale = getPointScale(camera, this);
      const highlightScale = object.highlighted ? 1.2 : 1;

      const newScale = new Vector3(1,1,1)
        .divide(parentScale)
        .multiplyScalar(objectScale)
        .multiplyScalar(highlightScale);

      object.scale.copy(newScale);
      object.geometry.computeBoundingSphere();
    });
  }
}
